import type { UnitType } from '@r6digital/rapidstor-sdk/domain';

import type RawDescriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import type RawUnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import type RawDeal from '@r6digital/rapidstor-types/types/Deal';

import type { UnitTypeWithImages } from '@/standard-unit-listing/screens';

/**
 * The two places this screen still needs a **raw** entity, quarantined here so the listing logic
 * itself stays entirely on the SDK's domain objects.
 *
 * Both are app-interop boundaries, not domain concerns: the booking store and the details modal are
 * typed against the flattened `UnitTypeWithImages` and the raw `Deal` that the original listing
 * produced. Rewriting those is a separate change from rebuilding the listing, so this module maps
 * back at the edge rather than letting raw entities leak inward.
 *
 * The SDK exposes no `.raw` escape hatch by design, so the mapping is by id against the descriptor
 * list the screen already holds.
 */

/**
 * Finds the raw descriptor a wrapped unit type belongs to, within the ones the screen was given.
 *
 * @param unitType - The wrapped unit type.
 * @param descriptors - The friendly descriptors the listing was built from.
 * @returns The owning descriptor, or `undefined` when it isn't among them.
 */
const ownerOf = (unitType: UnitType, descriptors: RawDescriptor[]): RawDescriptor | undefined => (
    descriptors.find((descriptor) => descriptor._id === unitType.descriptor?.id)
);

/**
 * Finds the raw unit type behind a wrapped one, within its owning descriptor.
 *
 * @param unitType - The wrapped unit type.
 * @param owner - The raw descriptor that owns it.
 * @returns The raw entity, or `undefined` when it isn't among that descriptor's unit types.
 */
const rawUnitTypeIn = (
    unitType: UnitType,
    owner: RawDescriptor | undefined,
): RawUnitType | undefined => (
    owner?.unitTypes.find((candidate) => candidate._id === unitType.id)
);

/**
 * Flattens a wrapped unit type back into the app's legacy `UnitTypeWithImages` shape.
 *
 * Interop only — the details modal and the booking store are typed against it.
 *
 * @param unitType - The wrapped unit type.
 * @param descriptors - The friendly descriptors the listing was built from.
 * @returns The legacy-shaped unit type, or `undefined` when the raw entity can't be located.
 */
export const toLegacyUnitType = (
    unitType: UnitType,
    descriptors: RawDescriptor[],
): UnitTypeWithImages | undefined => {
    const owner = ownerOf(unitType, descriptors);
    const raw = rawUnitTypeIn(unitType, owner);
    if (!raw || !owner) return undefined;

    return {
        ...raw,
        picture: owner.picture,
        slides: owner.slides,
        tags: owner.tags ?? [],
        description: owner.description ?? '',
        descriptor: owner,
    } as UnitTypeWithImages;
};

/**
 * The raw `Deal` the booking store expects for a selected deal id.
 *
 * Looked up in the descriptor list rather than rebuilt from the wrapped `Deal`: the store's consumers
 * (the details modal, the reservation flow) read `discountPlan` and `rates` off it, so a partial
 * reconstruction would typecheck and then fail at runtime.
 *
 * @param unitType - The wrapped unit type whose deals to search.
 * @param dealId - The selected deal id, or `null` for the standard price.
 * @param descriptors - The friendly descriptors the listing was built from.
 * @returns The raw deal, or `undefined` when nothing is selected or matched.
 */
export const rawDealFor = (
    unitType: UnitType,
    dealId: string | null,
    descriptors: RawDescriptor[],
): RawDeal | undefined => {
    if (!dealId) return undefined;
    const raw = rawUnitTypeIn(unitType, ownerOf(unitType, descriptors));
    return raw?.deals?.find((deal) => deal._id === dealId);
};
